/**
 * Die beiden Fahrzeuge der Station.
 *
 * Der eigentliche Reiz liegt im Unterschied: Das Schiff ist schneller und hat
 * mehr Reichweite, leidet aber unter schwerer See. Das U-Boot faehrt getaucht
 * und ist damit wetterunabhaengig — dafuer langsamer, kurzatmiger und an eine
 * maximale Tauchtiefe gebunden. Bei Sturm ist das langsamere Boot oft das
 * schnellere.
 */

export type SpeedSetting = 'slow' | 'cruise' | 'full';

export const SPEED_SETTINGS: readonly SpeedSetting[] = ['slow', 'cruise', 'full'];

export type VesselId = 'ship' | 'submarine';

export interface VesselProfile {
  readonly id: VesselId;
  readonly nameKey: string;
  readonly knots: Readonly<Record<SpeedSetting, number>>;
  readonly fuelPerHour: Readonly<Record<SpeedSetting, number>>;
  readonly fuelCapacity: number;
  /** Groesste Tiefe, in der noch gearbeitet werden kann. */
  readonly maxWorkingDepthMetres: number;
  /**
   * Benoetigte Wassertiefe unter dem Kiel. Entscheidet, ob eine Route ueber
   * die flachen Baenke der Bahamas ueberhaupt gefahren werden kann.
   */
  readonly draughtMetres: number;
  /** Getauchte Fahrzeuge spueren den Seegang nicht. */
  readonly weatherExposed: boolean;
  /** Wie stark schwere See den Rumpf angreift. */
  readonly hullFragility: number;
}

export const RESEARCH_SHIP: VesselProfile = {
  id: 'ship',
  nameKey: 'vessel.ship',
  knots: { slow: 9, cruise: 14, full: 19 },
  // Volle Fahrt kostet gut das Doppelte der Marschfahrt — die Reichweite
  // halbiert sich damit ungefaehr.
  fuelPerHour: { slow: 3, cruise: 6, full: 13 },
  fuelCapacity: 1000,
  /*
   * Bergung von der Oberflaeche aus: nur, was der Kran und die Taucher
   * schaffen.
   *
   * Das Dreieck ist im Median ueber 5.000 m tief — das Schiff kann hier also
   * praktisch nichts bergen, und das ist kein Ausgleichsfehler, sondern
   * Ozeanografie. Daraus folgt die Rollenteilung: **Das Schiff rettet
   * Menschen, das U-Boot holt die Beweise.** Und weil sich der Fall nicht
   * durch Retten allein loesen laesst, braucht man beide.
   */
  maxWorkingDepthMetres: 300,
  // Ein Forschungsschiff dieser Groesse braucht rund sechs Meter unter dem Kiel.
  draughtMetres: 6,
  weatherExposed: true,
  hullFragility: 1,
};

export const SUBMARINE: VesselProfile = {
  id: 'submarine',
  nameKey: 'vessel.submarine',
  knots: { slow: 6, cruise: 10, full: 14 },
  fuelPerHour: { slow: 2.5, cruise: 5, full: 11 },
  /*
   * Reichweite.
   *
   * Bei 600 Einheiten kam das U-Boot in Marschfahrt an 77 Prozent der Notrufe
   * heran und bei voller Fahrt nur an 382 Seemeilen — bei einer mittleren
   * Entfernung von 441. Es war damit kein zweites Werkzeug, sondern ein
   * schlechteres. Mit 900 reicht es weit genug, dass die Wahl wieder eine
   * zwischen Tempo, Wetterfestigkeit und Arbeitstiefe ist.
   */
  fuelCapacity: 900,
  // Erreicht praktisch jeden Punkt der Karte ausser dem Puerto-Rico-Graben.
  maxWorkingDepthMetres: 6000,
  // Getaucht flacher unterwegs, kann aber im Notfall auftauchen.
  draughtMetres: 4,
  weatherExposed: false,
  hullFragility: 0.35,
};

export const VESSEL_PROFILES: readonly VesselProfile[] = [RESEARCH_SHIP, SUBMARINE];

export function vesselProfile(id: VesselId): VesselProfile {
  const found = VESSEL_PROFILES.find((p) => p.id === id);
  if (found === undefined) {
    throw new Error(`Unbekanntes Fahrzeug: ${id}`);
  }
  return found;
}

/** Reichweite in Seemeilen bei voller Bunkerung und gegebener Fahrtstufe. */
export function rangeNm(profile: VesselProfile, setting: SpeedSetting): number {
  return (profile.fuelCapacity / profile.fuelPerHour[setting]) * profile.knots[setting];
}
